import React, { useState } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  TextField, 
  Button, 
  Grid, 
  MenuItem,
  InputAdornment,
  Snackbar,
  Alert,
  IconButton
} from '@mui/material';
import { 
  Add as AddIcon,
  PhotoCamera as CameraIcon,
  Diamond as DiamondIcon
} from '@mui/icons-material';
import { useAssets } from '../../contexts/AssetsContext';
import { useAuth } from '../../contexts/AuthContext';

const assetTypes = [
  { value: 'necklace', label: 'Necklace' },
  { value: 'ring', label: 'Ring' },
  { value: 'earrings', label: 'Earrings' },
  { value: 'bracelet', label: 'Bracelet' },
  { value: 'watch', label: 'Watch' },
  { value: 'tiara', label: 'Tiara' },
  { value: 'other', label: 'Other' }
];

const AddAssetForm = () => {
  const { addAsset } = useAssets();
  const { currentUser } = useAuth();
  
  // Johannesburg area locations for dropdown selection
  const johannesburgLocations = [
    { name: 'Sandton', coordinates: { lat: -26.107567, lng: 28.056702 } },
    { name: 'Rosebank', coordinates: { lat: -26.145738, lng: 28.092537 } },
    { name: 'Hyde Park', coordinates: { lat: -26.116543, lng: 28.034521 } },
    { name: 'Melrose Arch', coordinates: { lat: -26.131901, lng: 28.068121 } },
    { name: 'Fourways', coordinates: { lat: -26.023087, lng: 28.011439 } },
    { name: 'Braamfontein', coordinates: { lat: -26.192366, lng: 28.038582 } },
    { name: 'Johannesburg CBD', coordinates: { lat: -26.204103, lng: 28.047304 } }
  ];
  
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    description: '',
    value: '',
    location: johannesburgLocations[0].coordinates, // Default to Sandton
    locationName: johannesburgLocations[0].name,
    image: null
  });
  
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'value' ? Number(value) : value
    });
  };
  
  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      // In a real app, you would upload this to a server
      // For now, we'll just store the file object
      setFormData({
        ...formData,
        image: e.target.files[0]
      });
    }
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.name || !formData.type) {
      setSnackbar({
        open: true,
        message: 'Please fill in all required fields',
        severity: 'error'
      });
      return;
    }
    
    // Add the asset with the current user's ID
    const newAsset = addAsset({
      ...formData,
      ownerId: currentUser?.id || 'client1',
      status: 'normal'
    });
    
    // Show success message
    setSnackbar({
      open: true,
      message: 'Asset added successfully',
      severity: 'success'
    });
    
    // Reset form
    setFormData({
      name: '',
      type: '',
      description: '',
      value: '',
      location: { lat: -26.204103, lng: 28.047304 },
      image: null
    });
  };
  
  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };
  
  return (
    <Box sx={{ pb: 7 }}>
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <DiamondIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Add New Asset
          </Typography>
        </Box>
        
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                name="name"
                label="Asset Name"
                fullWidth
                required
                value={formData.name}
                onChange={handleInputChange}
                placeholder="e.g. Diamond Necklace"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                name="type"
                label="Asset Type"
                select
                fullWidth
                required
                value={formData.type}
                onChange={handleInputChange}
              >
                {assetTypes.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                name="value"
                label="Value (ZAR)"
                type="number"
                fullWidth
                value={formData.value}
                onChange={handleInputChange}
                InputProps={{
                  startAdornment: <InputAdornment position="start">R</InputAdornment>,
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                name="description"
                label="Description"
                multiline
                rows={3}
                fullWidth
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Describe your asset (color, size, distinguishing features, etc.)"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                name="locationName"
                label="Location in Johannesburg"
                select
                fullWidth
                value={formData.locationName}
                onChange={(e) => {
                  const selectedLocation = johannesburgLocations.find(loc => loc.name === e.target.value);
                  setFormData({
                    ...formData,
                    locationName: e.target.value,
                    location: selectedLocation.coordinates
                  });
                }}
              >
                {johannesburgLocations.map((option) => (
                  <MenuItem key={option.name} value={option.name}>
                    {option.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            
            <Grid item xs={12}>
              <Button
                variant="outlined"
                component="label"
                startIcon={<CameraIcon />}
                fullWidth
                sx={{ py: 1.5 }}
              >
                Upload Image
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={handleImageChange}
                />
              </Button>
              {formData.image && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Image selected: {formData.image.name}
                </Typography>
              )}
            </Grid>
            
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                startIcon={<AddIcon />}
                sx={{ py: 1.5 }}
              >
                Add Asset
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AddAssetForm;
